import { ReactNode } from 'react';
import {
  PersonalDataFormValues,
  ClinicalDataFormValues,
  ContactDataFormValues,
  AssessmentDataFormValues,
} from '@/app/(external system)/signup/form';

export type PatientStepKey = 'personal' | 'clinical' | 'assessment' | 'contact';

export interface serviceBlockType {
  label: string;
  description: string;
  area: string;
  methodology: 'Presencial' | 'Online' | 'Híbrido';
}

export interface serviceType extends serviceBlockType {
  faq: {
    id: string;
    title: string;
    children: ReactNode;
  }[];
  terms: {
    title: string;
    description: string | ReactNode;
  }[];
  steps: { key: PatientStepKey; label: string }[];
  signupRoute: string;
}

export interface TerapiaPatientForm {
  label: 'Terapia';
  personalData: PersonalDataFormValues;
  clinicalData: ClinicalDataFormValues;
  contactData: ContactDataFormValues;
}

export interface AvaliacaoNeuropsicologicaPatientForm {
  label: 'Avaliação Neuropsicológica';
  personalData: PersonalDataFormValues;
  assessmentData: AssessmentDataFormValues;
  contactData: ContactDataFormValues;
}

export type ServicePatientForm = TerapiaPatientForm | AvaliacaoNeuropsicologicaPatientForm;

const fixed_faq = [
  {
    id: 'o que é',
    title: 'O que é a SAMECA?',
    children: (
      <div className='flex w-full flex-col gap-4 p-4'>
        <h4>
          A SAMECA é uma associação sem fins lucrativos que reúne profissionais da saúde mental,
          dedicada a:
        </h4>
        <div className='flex flex-col gap-2 px-2'>
          <h4>
            <b className='font-semibold'>Organizar a classe desses profissionais</b>, dando força ao trabalho de classe;
          </h4>
          <h4>
            <b className='font-semibold'>Oferecer serviços para as pessoas pacientes</b>, criando uma ponte com a pessoa profissional mais compatível para seu caso específico.{' '}
          </h4>
        </div>
      </div>
    ),
  },
  {
    id: 'por que pagar',
    title: 'Por que pagar o Preço Regular (quando possível)?',
    children: (
      <div className='flex w-full flex-col gap-4 p-4'>
        <h4>
          Isso nos ajuda a manter o projeto ativo e a garantir atendimento para{' '}
          <b className='font-semibold'>quem pode pagar menos</b>, financia a formação contínua da
          nossa categoria, além de fortalecer nossa organização como classe.
          <br />
          <b className='font-semibold'>O Preço Regular é inviável neste momento?:</b> Informe quanto
          você poderia pagar por sessão o mais próximo possível do preço regular, e tentaremos
          encontrar entre nossos camaradas uma pessoa profissional que possa atender de acordo com o
          preço informado.
        </h4>
      </div>
    ),
  },
];

const services: serviceType[] = [
  {
    label: 'Terapia',
    description:
      'Atendimento psicológico voltado para questões emocionais, relacionamentos, ansiedade, autoestima, desenvolvimento pessoal e outras demandas relacionadas à saúde mental.',
    area: 'Psicologia',
    methodology: 'Online',
    steps: [
      { key: 'personal', label: 'Dados pessoais' },
      { key: 'contact', label: 'Contato' },
      { key: 'clinical', label: 'Informações clínicas' },
    ],
    signupRoute: '/signup/patients',
    faq: [
      fixed_faq[0],
      {
        id: '4',
        title: 'Posso inscrever uma pessoa terceira?',
        children: (
          <div className='flex w-full flex-col gap-1 p-4'>
            <h4>
              A SAMECA não aceita inscrições feitas por pessoas terceiras, exceto menores de idade
              cadastrados(as,es) por responsáveis legais. (Inscrições feitas por pessoas terceiras
              podem se configurar como atendimento compulsório).
            </h4>
          </div>
        ),
      },
      {
        id: '5',
        title: 'Sobre menores de idade',
        children: (
          <div className='flex w-full flex-col gap-1 p-4'>
            <h4>
              A idade mínima para o atendimento é de 13 anos de idade, e o (a,e) menor só consegue
              realizar atendimentos mediante autorização de um responsável legal (documento
              disponibilizado pelo profissional psicólogo). Pais que desejam inscrever seus filhos
              menores de idade devem informar que o (a,e) paciente é um tutelado na etapa de
              cadastro.
            </h4>
          </div>
        ),
      },
      {
        id: '6',
        title: 'Sobre atendimento presencial',
        children: (
          <div className='flex w-full flex-col gap-1 p-4'>
            <h4>
              Atualmente a SAMECA não realiza encaminhamentos presenciais, os atendimentos são
              majoritariamente online, exceções dependem do combinado entre paciente e terapeuta e
              sua relação.
            </h4>
          </div>
        ),
      },
    ],
    terms: [
      {
        title: '1. Organização do atendimento',
        description:
          'É de responsabilidade conjunta do (a,e) atendido (a,e) e terapeuta organizar os dias, horário, frequência, custos e tempo de atendimento;',
      },
      {
        title: '2. Respeito à pessoa profissional',
        description:
          'É de responsabilidade do (a,e) atendido (a,e) não expor seu (ua) terapeuta, respeitando o (a, e) profissional;',
      },
      {
        title: '3. Faltas injustificadas',
        description: 'Três faltas injustificadas significam exclusão automática do projeto;',
      },
      {
        title: '4. Problemas no atendimento',
        description: (
          <div>
            Qualquer problema/dificuldade com o (a, e) terapeuta deverá ser comunicado ao projeto
            via{' '}
            <a
              href='https://docs.google.com/forms/d/e/1FAIpQLSfiCfQSp_qhlBvAs4gL97x8ShzT3fJ2xfVx56egwJ91xoKP4g/viewform'
              target='_blank'
              className='text-text-accent underline transition-all hover:cursor-pointer hover:no-underline dark:text-success'
            >
              formulário de denúncia
            </a>
            ;
          </div>
        ),
      },
      {
        title: '5. Pagamento e continuidade do atendimento',
        description:
          'É de responsabilidade do (a,e) atendido (a,e) realizar o pagamento dos custos acordados com o (a,e) terapeuta, sabendo que o acúmulo de ausências do pagamento podem acarretar na interrupção do processo terapêutico;',
      },
    ],
  },
  {
    label: 'Avaliação Neuropsicológica',
    description:
      'Processo de avaliação das funções cognitivas, emocionais e comportamentais, auxiliando na compreensão de dificuldades e potencialidades.',
    area: 'Psicologia',
    methodology: 'Online',
    steps: [
      { key: 'personal', label: 'Dados pessoais' },
      { key: 'contact', label: 'Contato' },
      { key: 'assessment', label: 'Informações Clinicas' },
    ],
    signupRoute: '/signup/patients/assessment',
    faq: [
      {
        id: '1',
        title: 'O que é a SAMECA?',
        children: (
          <div className='flex w-full flex-col gap-4 p-4'>
            <h4>
              A SAMECA é uma associação sem fins lucrativos que reúne profissionais da saúde mental,
              dedicada a:
            </h4>
            <div className='flex flex-col gap-2 px-2'>
              <h4>
                <b className='font-semibold'>Organizar a classe desses profissionais</b>,
                dando força ao trabalho de classe;
              </h4>
              <h4>
                <b className='font-semibold'>Oferecer serviços para as pessoas pacientes</b>,
                criando uma ponte com a pessoa profissional mais compatível para seu caso específico.{' '}
              </h4>
            </div>
          </div>
        ),
      },
      {
        id: '4',
        title: 'Posso inscrever um terceiro?',
        children: (
          <div className='flex w-full flex-col gap-4 p-4'>
            A SAMECA não aceita inscrições feitas por terceiros, exceto menores de idade cadastrados
            por tutores legais. (Inscrições feitas por terceiros podem se configurar como
            atendimento compulsório).
          </div>
        ),
      },
      {
        id: '5',
        title: 'Sobre menores de idade',
        children: (
          <div className='flex w-full flex-col gap-4 p-4'>
            A idade mínima para o atendimento é de 13 anos de idade, e o menor só consegue ser
            atendido mediante autorização de um responsável (documento disponibilizado pelo
            psicólogo). Pais que desejam inscrever seus filhos menores de idade devem informar que o
            paciente é um tutelado na etapa de cadastro.
          </div>
        ),
      },
      {
        id: '6',
        title: 'Sobre atendimento presencial',
        children: (
          <div className='flex w-full flex-col gap-4 p-4'>
            Atualmente a SAMECA não realiza encaminhamentos presenciais, os atendimentos são
            majoritariamente online, exceções dependem do combinado entre paciente e terapeuta e sua
            relação.
          </div>
        ),
      },
      {
        id: '7',
        title: 'Como será o processo de avaliação neuropsicológica?',
        children: (
          <div className='flex w-full flex-col gap-4 p-4'>
            A duração estimada é de 6 a 10 sessões (entre 2 e 3 meses). Neste período ocorrem
            entrevistas e aplicações de testes e/ou escalas, de acordo os objetivos definidos e os
            resultados parciais. O processo será encerrado com a apresentação de um laudo, o qual
            conterá o detalhamento dos aspectos avaliados e possíveis desdobramentos.
          </div>
        ),
      },
    ],
    terms: [
      {
        title: '1. Organização do atendimento',
        description:
          'Acordos a respeito de assuntos como preço final, frequência, tempo de atendimento, dias, horários e/ou demais assuntos condizentes aos pormenores do processo de avaliação neuropsicológica deverão, obrigatoriamente, ser estabelecidos e firmados em comum acordo entre as partes envolvidas (i profissional e a pessoa que será atendida), assim como formalizados em contrato;',
      },
      {
        title: '2. Respeito à pessoa profissional',
        description:
          'É de responsabilidade da pessoa atendida tratar is profissionais do projeto com respeito e descrição, especialmente aqueles envolvidos diretamente no recebimento, triagem, encaminhamento e/ou atendimento da demanda. O descumprimento da regra poderá ocasionar na negativa em atender o caso e, em última instância, na proibição de futuras inscrições em quaisquer serviços ofertados pelo projeto;',
      },
      {
        title: '3. Correção da inscrição',
        description:
          'No caso de inconsistências ou falta de informações no preenchimento deste formulário, que impossibilitem o prosseguimento do processo, ume profissional da equipe de triagem e encaminhamentos entrará em contato via WhatsApp. Na ausência de resposta em até 7 dias corridos a partir do início do contato, a inscrição será automaticamente encerrada e retirada da fila;',
      },
      {
        title: '4. Problemas no atendimento',
        description: (
          <div>
            Is profissionais da SaMeCa tem como dever um tratamento respeitoso, acolhendo
            questionamentos e dúvidas de forma prestativa e esclarecedora, pertinentes ao serviço
            que será prestado, à profissão e/ou o projeto. Quaisquer problemas/dificuldades com i
            profissional deverão ser comunicadas ao projeto via{' '}
            <a
              href='https://docs.google.com/forms/d/e/1FAIpQLSfiCfQSp_qhlBvAs4gL97x8ShzT3fJ2xfVx56egwJ91xoKP4g/viewform'
              target='_blank'
              className='text-text-accent underline transition-all hover:cursor-pointer hover:no-underline dark:text-success'
            >
              formulário de denúncia
            </a>
            ;
          </div>
        ),
      },
      {
        title: '5. Alterações na inscrição',
        description:
          'Mudanças na disponibilidade do preço que poderá ser pago ou quaisquer informações previamente aqui expressadas estarão sujeitas a análise e aceite di profissional que receberá a demanda. Caso não seja possível prosseguir com as novas condições, a inscrição será encerrada e retirada da fila;',
      },
      {
        title: '6. Disponibilidade para início',
        description:
          'Ao finalizar o preenchimento e envio destas informações, a pessoa a ser atendida deverá ter disponibilidade para início imediato, não sendo possível fazer a reserva da vaga ou agendar um início para uma data posterior a 7 dias contados a partir do contato inicial. Caso não seja possível, a inscrição será encerrada e retirada da fila;',
      },
      {
        title: '7. Atendimento online',
        description:
          'Nossos contatos e atendimentos são feitos exclusivamente de forma remota e online, onde se faz necessário que as pessoas a serem atendidas tenham acesso e domínio básico de alguma ferramenta tecnológica que viabilize a comunicação entre as partes, assim como a realização dos atendimentos e serviços prestados. Exemplos: WhatsApp e aplicativos para chamadas de vídeo, como Google Meet, Zoom, Teams, FaceTime, entre outros;',
      },
      {
        title: '8. Nova inscrição',
        description:
          'Inscrições com impossibilidades de prosseguimento e/ou ausência de resposta aos contatos realizados pelo projeto e são encerradas e retiradas da fila conforme informado acima.',
      },
    ],
  },
];

export function useServices() {
  const getServiceByLabel = (label: string) => {
    return services.find((service) => service.label === label) || services[0];
  };
  const getServicesBlocks = () => {
    return services.map(({ label, description, area, methodology }) => ({
      label,
      description,
      area,
      methodology,
    }));
  };
  const getProfessionalTypes = () => {
    return [...new Set(services.map((s) => s.area))];
  };
  const getServicesByProfessionalType = (professionalType: string) => {
    return services
      .filter((s) => s.area === professionalType)
      .map(({ label, description, area, methodology }) => ({
        label,
        description,
        area,
        methodology,
      }));
  };

  return {
    getServiceByLabel,
    getServicesBlocks,
    getProfessionalTypes,
    getServicesByProfessionalType,
  };
}
